import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("vm_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const authApi = {
  signup: (data) => api.post("/auth/signup", data).then((r) => r.data),
  login: (data) => api.post("/auth/login", data).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  googleSession: (sessionId) =>
    api.post("/auth/google/session", null, { headers: { "X-Session-ID": sessionId } }).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
};

export const shopApi = {
  categories: () => api.get("/categories").then((r) => r.data),
  products: (params) => api.get("/products", { params }).then((r) => r.data),
  trending: (params) => api.get("/products/trending", { params }).then((r) => r.data),
  product: (slug) => api.get(`/products/${slug}`).then((r) => r.data),
  validateCoupon: (code) => api.post("/coupons/validate", null, { params: { code } }).then((r) => r.data),
  shippingOptions: () => api.get("/shipping/options").then((r) => r.data),
  quote: (payload) => api.post("/checkout/quote", payload).then((r) => r.data),
  checkout: (payload) => api.post("/checkout/create-session", payload).then((r) => r.data),
  paymentStatus: (sid) => api.get(`/payments/status/${sid}`).then((r) => r.data),
  completePayPal: (order_id) => api.post("/checkout/mock-paypal/complete", null, { params: { order_id } }).then((r) => r.data),
  wishlist: () => api.get("/wishlist").then((r) => r.data),
  toggleWishlist: (product_id) => api.post("/wishlist/toggle", null, { params: { product_id } }).then((r) => r.data),
  wishlistBundleSuggest: (product_ids) => api.post("/wishlist/bundle-suggest", { product_ids }).then((r) => r.data),
  myOrders: () => api.get("/orders/mine").then((r) => r.data),
  cancelOrder: (orderId) => api.post(`/orders/${orderId}/cancel`).then((r) => r.data),
  order: (id) => api.get(`/orders/${id}`).then((r) => r.data),
  // Q&A
  listQuestions: (product_id) => api.get(`/questions/${product_id}`).then((r) => r.data),
  askQuestion: (product_id, text) => api.post("/questions", { product_id, text }).then((r) => r.data),
  answerQuestion: (question_id, text) => api.post(`/questions/${question_id}/answers`, { question_id, text }).then((r) => r.data),
  voteQuestion: (question_id) => api.post(`/questions/${question_id}/vote`).then((r) => r.data),
  voteAnswer: (answer_id) => api.post(`/answers/${answer_id}/vote`).then((r) => r.data),
  // Loyalty
  loyaltyMe: () => api.get("/loyalty/me").then((r) => r.data),
  loyaltyTiers: () => api.get("/loyalty/tiers").then((r) => r.data),
  bundle: (slug) => api.get(`/bundles/${slug}`).then((r) => r.data),
  countdown: () => api.get("/settings/countdown").then((r) => r.data),
  reviewsWall: (limit = 6) => api.get("/reviews/wall", { params: { limit } }).then((r) => r.data),
  bestSellerIds: () => api.get("/products/best-seller-ids").then((r) => r.data),
  socialProof: (limit = 20) => api.get("/orders/social-proof", { params: { limit } }).then((r) => r.data),
  spin: (email) => api.post("/coupons/spin", { email }).then((r) => r.data),
  related: (productId, limit = 4) => api.get(`/products/${productId}/related`, { params: { limit } }).then((r) => r.data),
  // Referrals
  myReferral: () => api.get("/referrals/me").then((r) => r.data),
  applyReferral: (code) => api.post("/referrals/apply", { code }).then((r) => r.data),
};

export const adminApi = {
  stats: () => api.get("/admin/stats").then((r) => r.data),
  analytics: (days = 30) => api.get("/admin/analytics", { params: { days } }).then((r) => r.data),
  products: () => api.get("/admin/products").then((r) => r.data),
  saveProduct: (id, data) => id ? api.put(`/admin/products/${id}`, data).then(r => r.data) : api.post("/admin/products", data).then(r => r.data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`).then((r) => r.data),
  duplicateProduct: (id) => api.post(`/admin/products/${id}/duplicate`).then((r) => r.data),
  bulkDeleteProducts: (ids) => api.post("/admin/products/bulk-delete", { product_ids: ids }).then((r) => r.data),
  bulkUpdateProducts: (payload) => api.post("/admin/products/bulk-update", payload).then((r) => r.data),
  seedReviews: (ids, per = 3) => api.post("/admin/reviews/seed", { product_ids: ids, per_product: per }).then((r) => r.data),
  getCountdown: () => api.get("/settings/countdown").then((r) => r.data),
  saveCountdown: (payload) => api.put("/admin/settings/countdown", payload).then((r) => r.data),
  orders: () => api.get("/admin/orders").then((r) => r.data),
  fulfill: (id, status) => api.post(`/admin/orders/${id}/fulfill`, null, { params: { tracking_status: status } }).then((r) => r.data),
  suppliers: () => api.get("/admin/suppliers").then((r) => r.data),
  supplierCatalog: (supplier, params) => api.get(`/admin/suppliers/${supplier}/catalog`, { params }).then((r) => r.data),
  supplierDiagnose: (supplier) => api.get(`/admin/suppliers/${supplier}/diagnose`).then((r) => r.data),
  cjImportByUrl: (payload) => api.post("/admin/suppliers/cjdropshipping/import-by-url", payload).then((r) => r.data),
  aliexpressImportByUrl: (payload) => api.post("/admin/suppliers/aliexpress/import-by-url", payload).then((r) => r.data),
  importProduct: (payload) => api.post("/admin/suppliers/import", payload).then((r) => r.data),
  bulkImport: (payload) => api.post("/admin/suppliers/bulk-import", payload).then((r) => r.data),
  coupons: () => api.get("/admin/coupons").then((r) => r.data),
  saveCoupon: (data) => api.post("/admin/coupons", data).then((r) => r.data),
  reviews: () => api.get("/admin/reviews").then((r) => r.data),
  toggleReview: (id) => api.post(`/admin/reviews/${id}/toggle`).then((r) => r.data),
  questions: () => api.get("/admin/questions").then((r) => r.data),
  toggleQuestionHidden: (id) => api.post(`/admin/questions/${id}/toggle-hidden`).then((r) => r.data),
  toggleQuestionPin: (id) => api.post(`/admin/questions/${id}/toggle-pin`).then((r) => r.data),
  bundles: () => api.get("/admin/bundles").then((r) => r.data),
  saveBundle: (id, data) => id ? api.put(`/admin/bundles/${id}`, data).then((r) => r.data) : api.post("/admin/bundles", data).then((r) => r.data),
  deleteBundle: (id) => api.delete(`/admin/bundles/${id}`).then((r) => r.data),
  broadcast: (payload) => api.post("/admin/broadcast", payload).then((r) => r.data),
  listSubscribers: () => api.get("/admin/subscribers").then((r) => r.data),
  removeSubscriber: (email) => api.delete(`/admin/subscribers/${encodeURIComponent(email)}`).then((r) => r.data),
  getAnnouncement: () => api.get("/settings/announcement").then((r) => r.data),
  saveAnnouncement: (payload) => api.put("/admin/settings/announcement", payload).then((r) => r.data),
  csvImport: (payload) => api.post("/admin/products/csv-import", payload).then((r) => r.data),
};
